import * as React from 'react';
import {
  createRendererWithUniDriver,
  TPAComponentsWrapper,
} from '../../test/utils';
import { DropdownDriver, dropdownDriverFactory } from './Dropdown.driver';
import { Dropdown } from '.';
import {
  DropdownInputHandle,
  DropdownOptionProps,
  DropdownProps,
} from './types';
import { Text } from '../Text';
import Mock = jest.Mock;
import { act } from '@testing-library/react';
import { KeyDefinitionType } from '@wix/unidriver-core';
import { createOptionId } from './helpers';

describe('Dropdown', () => {
  jest.useFakeTimers();
  const render = createRendererWithUniDriver(dropdownDriverFactory);

  const baseProps = {
    'data-hook': 'data-hook',
  };

  const countryOptions = [
    { id: 'USA', value: 'United States', isSelectable: true },
    { id: 'UKR', value: 'Ukraine', isSelectable: true },
    { id: 'ISR', value: 'Israel', isSelectable: true },
    { id: 'BRA', value: 'Brazil', isSelectable: true },
    { id: 'JPN', value: 'Japan', isSelectable: true },
  ];

  it('should render', async () => {
    const { driver } = render(
      <Dropdown {...baseProps} options={[]} />,
      baseProps['data-hook'],
    );
    expect(await driver.exists()).toBe(true);
  });

  it('should set lang to root', async () => {
    const lang = 'he';
    const { driver } = render(<Dropdown lang={lang} options={[]} />);

    expect(await driver.getLang()).toBe(lang);
  });
  it('should use mobile design', async () => {
    const { driver } = render(
      TPAComponentsWrapper({ mobile: true })(
        <Dropdown {...baseProps} options={[]} />,
      ),
      baseProps['data-hook'],
    );
    expect(await driver.isMobile()).toBe(true);
  });

  it('should have 5 options when opened', async () => {
    const options: DropdownOptionProps[] = new Array(5)
      .fill(null)
      .map((_, i) => ({
        id: `${i}`,
        value: `value-${i}`,
      }));
    const { driver } = render(
      <Dropdown {...baseProps} options={options} />,
      baseProps['data-hook'],
    );
    await driver.click(); // open
    expect(await driver.getOptionsCount()).toBe(5);
  });

  it('should not have aria-label attribute when not received by props', async () => {
    const { driver } = render(
      <Dropdown {...baseProps} options={[]} />,
      baseProps['data-hook'],
    );
    expect(await driver.getAriaLabel()).toBe(null);
  });

  it('should have aria-label attribute when received by props', async () => {
    const ariaLabelContent = 'Wubba lubba';
    const { driver } = render(
      <Dropdown {...baseProps} aria-label={ariaLabelContent} options={[]} />,
      baseProps['data-hook'],
    );
    expect(await driver.getAriaLabel()).toBe(ariaLabelContent);
  });

  it('should not have aria-labelledby attribute when not received by props', async () => {
    const { driver } = render(
      <Dropdown {...baseProps} options={[]} />,
      baseProps['data-hook'],
    );
    expect(await driver.getAriaLabelledBy()).toBe(null);
  });

  it('should have aria-labelledby attribute when received by props', async () => {
    const ariaLabelledByContent = 'Wubba lubba';
    const { driver } = render(
      <Dropdown
        {...baseProps}
        aria-labelledby={ariaLabelledByContent}
        options={[]}
      />,
      baseProps['data-hook'],
    );
    expect(await driver.getAriaLabelledBy()).toBe(ariaLabelledByContent);
  });

  it('should have aria-invalid="true" when error is passed', async () => {
    const { driver } = render(
      <Dropdown {...baseProps} options={[]} error />,
      baseProps['data-hook'],
    );
    expect(await driver.getAriaInvalid()).toBe('true');
  });

  it('should have aria-invalid="false" when error is passed with false', async () => {
    const { driver } = render(
      <Dropdown {...baseProps} options={[]} error={false} />,
      baseProps['data-hook'],
    );
    expect(await driver.getAriaInvalid()).toBe('false');
  });

  it('should not have aria-invalid attribute when error is not passed', async () => {
    const { driver } = render(
      <Dropdown {...baseProps} options={[]} />,
      baseProps['data-hook'],
    );
    expect(await driver.getAriaInvalid()).toBe(null);
  });

  it('should have label for id attribute', async () => {
    const labelText = 'labelText';
    const { driver } = render(
      <Dropdown {...baseProps} label={labelText} options={[]} />,
      baseProps['data-hook'],
    );

    const labelForInput = await driver.getLabelForInput();
    const dropdownId = await driver.getDropdownId();

    expect(await driver.getLabelText()).toEqual(labelText);
    expect(labelForInput).toEqual(dropdownId);
  });

  it('should have label with asterisk when aria-required and showAsterisk are true', async () => {
    const labelText = 'labelText';
    const { driver } = render(
      <Dropdown
        {...baseProps}
        options={[]}
        label={labelText}
        aria-required
        showAsterisk
      />,
      baseProps['data-hook'],
    );

    const labelAsterisk = await driver.getLabelAsterisk();
    expect(await labelAsterisk.exists()).toBe(true);
  });

  it('should not have label with asterisk when only aria-required is true', async () => {
    const labelText = 'labelText';
    const { driver } = render(
      <Dropdown {...baseProps} options={[]} label={labelText} aria-required />,
      baseProps['data-hook'],
    );

    const labelAsterisk = await driver.getLabelAsterisk();
    expect(await labelAsterisk.exists()).toBe(false);
  });

  it('should consume dropdownId for a11y wiring with custom label', async () => {
    const customDropdownId = 'my-dropdown';
    const { driver } = render(
      <Dropdown {...baseProps} options={[]} dropdownId={customDropdownId} />,
      baseProps['data-hook'],
    );
    expect(await driver.getDropdownId()).toBe(customDropdownId);
  });

  it('should use optionsContainerId when received by props', async () => {
    const optionsContainerId = 'the-best-id-ever';
    const options = new Array(5).fill(null).map((_, i) => ({
      id: `${i}`,
      value: `value-${i}`,
      isSelectable: i < 3,
    }));
    const { driver } = render(
      <Dropdown
        {...baseProps}
        optionsContainerId={optionsContainerId}
        options={options}
      />,
      baseProps['data-hook'],
    );

    await driver.click();
    await driver.keydown('ArrowDown');
    expect(await driver.getAriaActivedescendant()).toBe(
      createOptionId(optionsContainerId, options[0].id),
    );
  });

  it('should toggle aria expanded according to dropdown state', async () => {
    const options = new Array(5).fill(null).map((_, i) => ({
      id: `${i}`,
      value: `value-${i}`,
      isSelectable: i < 3,
    }));
    const { driver } = render(
      <Dropdown {...baseProps} options={options} />,
      baseProps['data-hook'],
    );

    expect(await driver.getAriaExpanded()).toBe('false');
    await driver.click(); // open
    expect(await driver.getAriaExpanded()).toBe('true');
    await driver.selectOptionAt(0);
    expect(await driver.getAriaExpanded()).toBe('false');
  });

  it('should be focused', async () => {
    const dropdownRef = React.createRef<HTMLButtonElement>();

    const { driver } = render(
      <Dropdown {...baseProps} options={[]} ref={dropdownRef} />,
      baseProps['data-hook'],
    );

    dropdownRef.current.focus();
    expect(await driver.isFocused()).toBe(true);
  });

  it('should update the selected option when `options` prop changes externally', async () => {
    const initialSelectedValue = 'Initial value';
    const initialSelectedIndex = 1;
    const initialSelectedId = `${initialSelectedIndex}`;
    const updatedSelectedValue = 'New value';

    const DropdownWrapper = () => {
      const [selectedValue, setSelectedValue] =
        React.useState(initialSelectedValue);
      React.useEffect(() => {
        setTimeout(() => setSelectedValue(updatedSelectedValue), 0);
      }, []);

      const options = [
        { id: '0', value: 'Input Text 0', isSelectable: true },
        { id: '1', value: selectedValue, isSelectable: true },
        { id: '2', value: 'Input Text 2', isSelectable: true },
      ];

      return (
        <>
          <Dropdown
            {...baseProps}
            initialSelectedId={initialSelectedId}
            options={options}
          />
        </>
      );
    };

    await act(async () => {
      const { driver } = render(<DropdownWrapper />, baseProps['data-hook']);
      await driver.selectOptionAt(initialSelectedIndex);
      expect(await driver.getDropdownText()).toBe(initialSelectedValue);

      jest.runAllTimers();
      expect(await driver.getDropdownText()).toBe(updatedSelectedValue);
    });
  });

  it('should not keep the old selected option when `options` prop changes with different ids externally', async () => {
    const initialSelectedValue = 'Initial value';
    const initialSelectedId = `1`;
    const placeholderText = 'Placeholder Text';
    const effectFn = jest.fn();

    const DropdownWrapper = () => {
      const [options, setOptions] = React.useState([
        { id: '0', value: 'Input Text 1', isSelectable: true },
        {
          id: initialSelectedId,
          value: initialSelectedValue,
          isSelectable: true,
        },
        { id: '2', value: 'Input Text 3', isSelectable: true },
        { id: '3', value: 'Input Text 4', isSelectable: false },
        { divider: true },
        { id: '5', value: 'Input Text 6', isSelectable: false },
      ]);

      React.useEffect(() => {
        setTimeout(() => {
          effectFn();
          setOptions([
            { id: '10', value: 'Input Text 7', isSelectable: true },
            { id: '20', value: 'Input Text 8', isSelectable: true },
            { id: '30', value: 'Input Text 9', isSelectable: true },
            { id: '40', value: 'Input Text 10', isSelectable: false },
          ]);
        }, 0);
      }, []);

      return (
        <>
          <Dropdown
            {...baseProps}
            placeholder={placeholderText}
            initialSelectedId={initialSelectedId}
            options={options}
          />
        </>
      );
    };
    await act(async () => {
      const { driver } = render(<DropdownWrapper />, baseProps['data-hook']);
      expect(await driver.getDropdownText()).toBe(initialSelectedValue);
      expect(effectFn).not.toHaveBeenCalled();
      jest.runAllTimers();
      expect(effectFn).toHaveBeenCalledTimes(1);
      expect(await driver.getDropdownText()).toBe(placeholderText);
    });
  });

  it('should update selected option when selected id changes', async () => {
    const DropdownWrapper = () => {
      const [selectedId, setSelectedId] = React.useState(countryOptions[0].id);

      React.useEffect(() => {
        setTimeout(() => setSelectedId(countryOptions[1].id), 0);
      }, []);

      return (
        <Dropdown
          {...baseProps}
          options={countryOptions}
          selectedId={selectedId}
        />
      );
    };

    await act(async () => {
      const { driver } = render(<DropdownWrapper />, baseProps['data-hook']);
      expect(await driver.getDropdownText()).toBe(countryOptions[0].value);
      jest.runAllTimers();
      expect(await driver.getDropdownText()).toBe(countryOptions[1].value);
    });
  });

  it('should update empty selected option when selected id changes', async () => {
    const DropdownWrapper = () => {
      const [selectedId, setSelectedId] = React.useState('-');

      React.useEffect(() => {
        setTimeout(() => setSelectedId(countryOptions[1].id), 0);
      }, []);

      return (
        <Dropdown
          {...baseProps}
          options={countryOptions}
          selectedId={selectedId}
        />
      );
    };

    await act(async () => {
      const { driver } = render(<DropdownWrapper />, baseProps['data-hook']);
      expect(await driver.getDropdownText()).toBe('');
      jest.runAllTimers();
      expect(await driver.getDropdownText()).toBe(countryOptions[1].value);
    });
  });

  it('should prefer controllable selected id', async () => {
    await act(async () => {
      const { driver } = render(
        <Dropdown
          options={countryOptions}
          selectedId={countryOptions[0].id}
          initialSelectedId={countryOptions[1].id}
          {...baseProps}
        />,
        baseProps['data-hook'],
      );
      expect(await driver.getDropdownText()).toBe(countryOptions[0].value);
    });
  });

  it('should select the provided initialSelectedId', async () => {
    const { driver } = render(
      <Dropdown
        options={countryOptions}
        initialSelectedId={countryOptions[1].id}
        {...baseProps}
      />,
      baseProps['data-hook'],
    );
    expect(await driver.getDropdownText()).toBe(countryOptions[1].value);
  });

  it('should have null value if options do not find with such id', async () => {
    const testOptions = [
      { id: 'USA', value: 'United States', isSelectable: true },
    ];
    await act(async () => {
      const { driver } = render(
        <Dropdown options={testOptions} selectedId="JPN" {...baseProps} />,
        baseProps['data-hook'],
      );
      expect(await driver.getDropdownText()).toBe('');
    });
  });

  it('should empty dropdown text after option with selected id disappears', async () => {
    const DropdownWrapper = () => {
      const [options, setOptions] = React.useState(countryOptions);

      React.useEffect(() => {
        setTimeout(() => setOptions([]), 0);
      }, []);

      return (
        <Dropdown
          {...baseProps}
          options={options}
          selectedId={countryOptions[0].id}
        />
      );
    };

    await act(async () => {
      const { driver } = render(<DropdownWrapper />, baseProps['data-hook']);
      expect(await driver.getDropdownText()).toBe(countryOptions[0].value);
      jest.runAllTimers();
      expect(await driver.getDropdownText()).toBe('');
    });
  });

  it('should have a loader when isLoading is true - desktop', async () => {
    await act(async () => {
      const { driver } = render(
        <Dropdown options={[]} isLoading {...baseProps} />,
        baseProps['data-hook'],
      );
      expect(await driver.getIsLoading()).toBe(true);
    });
  });

  describe('error states', () => {
    it('should have error', async () => {
      const { driver } = render(
        <Dropdown {...baseProps} options={[]} error />,
        baseProps['data-hook'],
      );
      expect(await driver.hasError()).toBeTruthy();
      expect(await driver.hasErrorMessage()).toBeFalsy();
    });

    it('should have error message', async () => {
      const errorMessage = 'Ooops, something went wrong';
      const { driver } = render(
        <Dropdown
          {...baseProps}
          options={[]}
          error
          errorMessage={errorMessage}
        />,
        baseProps['data-hook'],
      );
      await driver.clickOnErrorIcon();
      expect(await driver.hasError()).toBeTruthy();
      expect(await driver.hasErrorMessage()).toBeTruthy();
      expect(await driver.getErrorMessageContent()).toMatch(errorMessage);
    });
  });

  describe('onChange', () => {
    let driver: DropdownDriver;
    let onChange: Mock;
    const placeholder = 'placeholder';
    const options = new Array(5).fill(null).map((_, i) => ({
      id: `${i}`,
      value: `value-${i}`,
      isSelectable: i < 3,
    }));

    beforeEach(async () => {
      onChange = jest.fn();
      driver = render(
        <Dropdown
          {...baseProps}
          options={options}
          onChange={onChange}
          placeholder={placeholder}
        />,
        baseProps['data-hook'],
      ).driver;
      await driver.click(); // open
    });

    it('should be called when selection changed', async () => {
      expect(await driver.getDropdownText()).toEqual(placeholder);
      await driver.selectOptionAt(0);
      expect(await driver.getDropdownText()).toEqual(options[0].value);
      expect(onChange).toHaveBeenCalledWith(options[0]);
    });

    it('should not be called when readonly is true', async () => {
      const { driver } = render(
        <Dropdown
          {...baseProps}
          options={options}
          onChange={onChange}
          placeholder={placeholder}
          readOnly
        />,
        baseProps['data-hook'],
      );
      await driver.selectOptionAt(0);
      expect(onChange).not.toHaveBeenCalled();
    });

    it('should not be called when selecting disabled item', async () => {
      await driver.selectOptionAt(4);
      expect(onChange).not.toHaveBeenCalled();
    });

    it('should not be called when re-selecting a selected item', async () => {
      await driver.selectOptionAt(0);
      await driver.click(); // open
      await driver.selectOptionAt(0);
      expect(onChange).toHaveBeenCalledTimes(1);
    });

    it('should not be called when isLoading is true', async () => {
      const { driver } = render(
        <Dropdown
          {...baseProps}
          options={options}
          onChange={onChange}
          placeholder={placeholder}
          isLoading
        />,
        baseProps['data-hook'],
      );
      await driver.selectOptionAt(0);
      expect(onChange).not.toHaveBeenCalled();
    });
  });

  describe('onExpandedChange', () => {
    let driver: DropdownDriver;
    let onExpandedChange: Mock;
    const placeholder = 'placeholder';
    const options = new Array(5).fill(null).map((_, i) => ({
      id: `${i}`,
      value: `value-${i}`,
      isSelectable: i < 3,
    }));

    beforeEach(() => {
      onExpandedChange = jest.fn();
      driver = render(
        <Dropdown
          {...baseProps}
          options={options}
          onExpandedChange={onExpandedChange}
          placeholder={placeholder}
        />,
        baseProps['data-hook'],
      ).driver;
    });

    it('should be called with true when clicked', async () => {
      await driver.click();

      expect(onExpandedChange).toBeCalledTimes(1);
      expect(onExpandedChange).toBeCalledWith(true);
    });

    it('should be called with false when clicked twice', async () => {
      await driver.click();

      onExpandedChange.mockClear();

      await driver.click();

      expect(onExpandedChange).toBeCalledTimes(1);
      expect(onExpandedChange).toBeCalledWith(false);
    });

    it('should not crush if undefined is passed', async () => {
      driver = render(
        <Dropdown {...baseProps} options={options} placeholder={placeholder} />,
        baseProps['data-hook'],
      ).driver;

      expect(await driver.exists()).toBe(true);
    });
  });

  describe('disabled', () => {
    it('should be disabled', async () => {
      const { driver } = render(
        <Dropdown {...baseProps} options={[]} disabled />,
        baseProps['data-hook'],
      );
      expect(await driver.isDisabled()).toBe(true);
    });
  });

  describe('unavailable', () => {
    let driver: DropdownDriver;
    const options: DropdownOptionProps[] = [
      {
        id: `0`,
        value: `value-0`,
        isSelectable: true,
      },
      {
        id: `1`,
        value: `value-1`,
        unavailable: true,
      },
    ];

    it('should be selectable', async () => {
      const onChange = jest.fn();
      driver = render(
        <Dropdown {...baseProps} options={options} onChange={onChange} />,
        baseProps['data-hook'],
      ).driver;
      await driver.click(); // open
      await driver.selectOptionAt(1);
      expect(onChange).toHaveBeenCalledWith(options[1]);
    });
  });

  describe('suffix', () => {
    let driver: DropdownDriver;
    const options: DropdownOptionProps[] = [
      {
        id: `0`,
        value: `value-0`,
        isSelectable: true,
        suffix: <Text data-hook="suffix" />,
      },
      {
        id: `1`,
        value: `value-1`,
        unavailable: true,
      },
    ];

    beforeEach(async () => {
      driver = render(
        <Dropdown {...baseProps} options={options} />,
        baseProps['data-hook'],
      ).driver;
      await driver.click(); // open
    });

    it('should render suffix if exist', async () => {
      expect(await (await driver.getSuffixAt(0)).exists()).toBe(true);
    });
  });

  describe('error tooltip', () => {
    it('should toggle error tooltip when clicking on error icon', async () => {
      const { driver } = render(
        <Dropdown
          {...baseProps}
          options={[]}
          error
          errorMessage="error Message"
        />,
        baseProps['data-hook'],
      );
      expect(await driver.isErrorTooltipShown()).toBe(false);

      await driver.clickOnErrorIcon();

      jest.runAllTimers();

      expect(await driver.isErrorTooltipShown()).toBe(true);

      await driver.clickOnErrorIcon();

      jest.runAllTimers();

      expect(await driver.isErrorTooltipShown()).toBe(false);
    });

    it('should set the correct a11y attributes', async () => {
      const { driver } = render(
        <Dropdown
          {...baseProps}
          options={[]}
          error
          errorMessage="error Message"
        />,
        baseProps['data-hook'],
      );

      await driver.clickOnErrorIcon();

      jest.runAllTimers();

      expect(await driver.isErrorTooltipShown()).toBeTruthy();

      const ariaDescribedBy = await driver.getAriaDescribedBy();
      expect(ariaDescribedBy).toBeDefined();
      const errorMessage = document.querySelector(`#${ariaDescribedBy}`);

      expect(errorMessage).not.toBeNull();
      expect(errorMessage.textContent).toBe('error Message');
    });

    it('aria describedby should be concatenated to value from props', async () => {
      const { driver } = render(
        <Dropdown
          {...baseProps}
          options={[]}
          error
          errorMessage="error Message"
          aria-describedby="some descriptions"
        />,
        baseProps['data-hook'],
      );

      await driver.clickOnErrorIcon();

      jest.runAllTimers();

      expect(await driver.isErrorTooltipShown()).toBeTruthy();
      expect(await driver.getAriaDescribedBy()).toContain('some descriptions');

      expect(await driver.getErrorMessageContent()).toBe('error Message');
    });
  });

  describe('a11y', () => {
    describe('keyboard interactions', () => {
      const options = new Array(13).fill(null).map((_, i) => ({
        id: `${i}`,
        value: `value-${i}`,
        isSelectable: i < 8 && i !== 4,
      }));
      const optionsContainerId = 'options-container';

      describe('combobox', () => {
        it.each(['ArrowDown', 'Enter', 'Space'])(
          'should open dropdown without focusing on option on %s',
          async (keydown: KeyDefinitionType) => {
            const { driver } = render(
              <Dropdown
                {...baseProps}
                options={options}
                dropdownId="dropdownId"
                optionsContainerId={optionsContainerId}
              />,
              baseProps['data-hook'],
            );
            await driver.keydown(keydown);
            expect(await driver.getAriaActivedescendant()).toBe(null);
          },
        );

        it.each(['ArrowUp', 'Home'])(
          'should open dropdown focusing on first option on %s',
          async (keydown: KeyDefinitionType) => {
            const { driver } = render(
              <Dropdown
                {...baseProps}
                options={options}
                dropdownId="dropdownId"
                optionsContainerId={optionsContainerId}
              />,
              baseProps['data-hook'],
            );

            await driver.keydown(keydown);
            expect(await driver.getAriaActivedescendant()).toBe(
              createOptionId(optionsContainerId, options[0].id),
            );
          },
        );

        it.each(['ArrowDown', 'ArrowUp', 'Enter', 'Space'])(
          'should not change focus selection on %s',
          async (keydown: KeyDefinitionType) => {
            const { driver } = render(
              <Dropdown
                {...baseProps}
                options={options}
                dropdownId="dropdownId"
                optionsContainerId={optionsContainerId}
              />,
              baseProps['data-hook'],
            );

            await driver.keydown('ArrowDown');
            await driver.keydown('ArrowDown');
            await driver.keydown('ArrowDown');
            await driver.keydown('ArrowDown');

            expect(await driver.getAriaActivedescendant()).toEqual(
              createOptionId(optionsContainerId, options[2].id),
            );

            await driver.keydown('Escape');
            await driver.keydown(keydown);

            const descendantAfter = await driver.getAriaActivedescendant();
            expect(descendantAfter).toEqual(
              createOptionId(optionsContainerId, options[2].id),
            );
          },
        );

        it(`should focus on last option on End`, async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={options}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('End');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, options[12].id),
          );
        });

        it(`should change focus to first option on Home`, async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={options}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
              initialSelectedId={options[2].id}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('Home');
          const descendantAfter = await driver.getAriaActivedescendant();
          expect(descendantAfter).toEqual(
            createOptionId(optionsContainerId, options[0].id),
          );
        });

        it(`should change focus to last option on End`, async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={options}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
              initialSelectedId={options[2].id}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('End');
          const descendantAfter = await driver.getAriaActivedescendant();
          expect(descendantAfter).toEqual(
            createOptionId(optionsContainerId, options[12].id),
          );
        });
      });

      describe('listbox', () => {
        it('should select a focused option on space', async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={options}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown(' ');
          const text = await driver.getDropdownText();
          expect(text).toEqual(options[2].value);
          expect(await driver.isFocused()).toBe(true);
        });

        it('should not select a disabled option on space', async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={options}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown(' ');
          expect(await driver.getDropdownText()).toEqual(options[3].value);
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown(' ');
          expect(await driver.getDropdownText()).toEqual(options[3].value);
        });

        it('should move focus on ArrowDown', async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={options}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');

          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, options[2].id),
          );
        });

        it('should move focus on ArrowUp', async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={options}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowUp');
          await driver.keydown('ArrowUp');

          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, options[4].id),
          );
        });

        it('should increase focus index by 10 on PageDown', async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={options}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('PageDown');

          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, options[10].id),
          );
        });

        it('should decrease focus index by 10 on PageUp', async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={options}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('PageUp');

          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, options[1].id),
          );
        });

        it('should not change focus from last option on ArrowDown', async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={options}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('ArrowDown');
          await driver.keydown('PageDown');
          await driver.keydown('PageDown');

          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, options[12].id),
          );
          await driver.keydown('ArrowDown');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, options[12].id),
          );
        });

        it('should not change focus from last option on PageDown', async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={options}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('ArrowDown');
          await driver.keydown('PageDown');
          await driver.keydown('PageDown');

          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, options[12].id),
          );
          await driver.keydown('PageDown');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, options[12].id),
          );
        });

        it('should not change focus from first option on ArrowUp', async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={options}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowUp');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, options[0].id),
          );
        });

        it('should not change focus from first option on PageUp', async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={options}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, options[0].id),
          );
          await driver.keydown('PageUp');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, options[0].id),
          );
        });

        it('should move focus to first option on Home', async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={options}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, options[3].id),
          );
          await driver.keydown('Home');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, options[0].id),
          );
        });

        it('should move focus to last option on End', async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={options}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          await driver.keydown('ArrowDown');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, options[3].id),
          );
          await driver.keydown('End');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, options[12].id),
          );
        });
      });

      describe('letters', () => {
        const optionsLettered = [
          { id: '0', value: 'apple', isSelectable: true },
          { id: '1', value: 'banana', isSelectable: true },
          {
            id: '2',
            value: 'blueberry',
            isSelectable: true,
            unavailable: true,
          },
          { id: '3', value: 'boysenberry', isSelectable: true },
          { id: '4', value: 'cherry', isSelectable: true },
          { id: '5', value: 'durian', isSelectable: false },
          { divider: true },
          { id: '6', value: 'Eggfruit', isSelectable: true },
          { id: '7', value: 'eggplant', isSelectable: true },
          { id: '8', value: 'elderberry', isSelectable: false },
          { id: '9', value: 'grape', isSelectable: true, isDisabled: true },
          { id: '10', value: 'honeydew', isSelectable: true },
          { id: '11', value: 'italian plum', isSelectable: true },
          { id: '12', value: 'indian fig', isSelectable: true },
        ];

        it('should find first match on letter keydown', async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={optionsLettered}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('ArrowDown');
          await driver.keydown('e');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, optionsLettered[7].id),
          );
        });

        it('should focus on next match on letter keydown', async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={optionsLettered}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('ArrowDown');
          await driver.keydown('e');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, optionsLettered[7].id),
          );
          await driver.keydown('e');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, optionsLettered[8].id),
          );
        });

        it('should iterate to the first match after the last match had focus', async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={optionsLettered}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('b');
          await driver.keydown('b');
          await driver.keydown('b');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, optionsLettered[3].id),
          );
          await driver.keydown('b');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, optionsLettered[1].id),
          );
        });

        it('should not change focus if match has not been found', async () => {
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={optionsLettered}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('ArrowDown');
          await driver.keydown('b');
          await driver.keydown('b');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, optionsLettered[2].id),
          );
          await driver.keydown('z');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, optionsLettered[2].id),
          );
        });

        it('should go to the first match if multiple letters are quickly typed and focused option starts with the same letter', async () => {
          // current focus - boysenberry. if "ba" is quickly typed it should go to banana.
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={optionsLettered}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('b');
          await driver.keydown('ArrowDown');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, optionsLettered[2].id),
          );
          await driver.keydown('b');
          await driver.keydown('a');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, optionsLettered[1].id),
          );
        });

        it('should not break if option id contains special characters', async () => {
          const specialChars = [' ', ':', '.', '+'];
          const optionsWithSpecialChars = optionsLettered.map((opt, index) =>
            opt.id ? { ...opt, id: opt.id + specialChars[index % 4] } : opt,
          );
          const { driver } = render(
            <Dropdown
              {...baseProps}
              options={optionsWithSpecialChars}
              dropdownId="dropdownId"
              optionsContainerId={optionsContainerId}
            />,
            baseProps['data-hook'],
          );
          await driver.keydown('h');
          expect(await driver.getAriaActivedescendant()).toEqual(
            createOptionId(optionsContainerId, optionsWithSpecialChars[11].id),
          );
        });
      });
    });

    it('should focus when option is selected with click', async () => {
      const options = new Array(5).fill(null).map((_, i) => ({
        id: `${i}`,
        value: `value-${i}`,
        isSelectable: i < 3,
      }));

      const { driver } = render(
        <Dropdown {...baseProps} options={options} />,
        baseProps['data-hook'],
      );
      await driver.click();
      await driver.selectOptionAt(0);
      expect(await driver.isFocused()).toBe(true);
    });

    it('should set aria-hidden to true for divider options', async () => {
      const options = new Array(6).fill(null).map((_, i) =>
        i < 3
          ? {
              id: `${i}`,
              value: `value-${i}`,
            }
          : { divider: true },
      );

      const { driver } = render(
        <Dropdown {...baseProps} options={options} />,
        baseProps['data-hook'],
      );
      await driver.click();
      for (let i = 0; i < options.length; i++) {
        expect((await driver.getOptionAt(i)).isHidden()).toBe(!(i < 3));
      }
    });
  });
});

describe('Native Dropdown', () => {
  const render = createRendererWithUniDriver(dropdownDriverFactory);

  const baseProps = {
    'data-hook': 'data-hook',
  };

  const NativeConfiguredDropdown = React.forwardRef<
    DropdownInputHandle,
    Partial<DropdownProps>
  >((props, ref) =>
    TPAComponentsWrapper({ mobile: true })(
      <Dropdown
        {...baseProps}
        options={[]}
        mobileNativeSelect
        ref={ref}
        {...props}
      />,
    ),
  );

  it('should render native', async () => {
    const { driver } = render(
      <NativeConfiguredDropdown />,
      baseProps['data-hook'],
    );
    expect(await driver.isNativeSelect()).toBe(true);
  });

  it('should not render native on desktop', async () => {
    const { driver } = render(
      TPAComponentsWrapper({ mobile: false })(
        <Dropdown options={[]} mobileNativeSelect />,
      ),
      baseProps['data-hook'],
    );
    expect(await driver.isNativeSelect()).toBe(false);
  });

  it('should have 5 options when opened', async () => {
    const options: DropdownOptionProps[] = new Array(5)
      .fill(null)
      .map((_, i) => ({
        id: `${i}`,
        value: `value-${i}`,
      }));
    const { driver } = render(
      <NativeConfiguredDropdown options={options} />,
      baseProps['data-hook'],
    );
    expect(await driver.getOptionsCount()).toBe(5);
  });

  it('should not have aria-label attribute when not received by props', async () => {
    const { driver } = render(
      <NativeConfiguredDropdown />,
      baseProps['data-hook'],
    );
    expect(await driver.getAriaLabel()).toBe(null);
  });

  it('should have aria-label attribute when received by props', async () => {
    const ariaLabelContent = 'Wubba lubba';
    const { driver } = render(
      <NativeConfiguredDropdown aria-label={ariaLabelContent} />,
      baseProps['data-hook'],
    );
    expect(await driver.getAriaLabel()).toBe(ariaLabelContent);
  });

  it('should not have aria-labelledby attribute when not received by props', async () => {
    const { driver } = render(
      <NativeConfiguredDropdown />,
      baseProps['data-hook'],
    );
    expect(await driver.getAriaLabelledBy()).toBe(null);
  });

  it('should have aria-labelledby attribute when received by props', async () => {
    const ariaLabelledByContent = 'Wubba lubba';
    const { driver } = render(
      <NativeConfiguredDropdown aria-labelledby={ariaLabelledByContent} />,
      baseProps['data-hook'],
    );
    expect(await driver.getAriaLabelledBy()).toBe(ariaLabelledByContent);
  });

  it('should be focused', async () => {
    const nativeDropdownRef = React.createRef<HTMLButtonElement>();
    const { driver } = render(
      <NativeConfiguredDropdown ref={nativeDropdownRef} />,
      baseProps['data-hook'],
    );

    nativeDropdownRef.current.focus();
    expect(await driver.isFocused()).toBe(true);
  });

  it('should have a loader when isLoading is true - mobile', async () => {
    const { driver } = render(
      <NativeConfiguredDropdown isLoading options={[]} />,
      baseProps['data-hook'],
    );
    expect(await driver.getIsLoading()).toBe(true);
  });

  describe('onChange', () => {
    let driver: DropdownDriver;
    let onChange: Mock;
    const placeholder = 'placeholder';
    const options = new Array(5).fill(null).map((_, i) => ({
      id: `${i}`,
      value: `value-${i}`,
      isSelectable: i < 3,
    }));

    beforeEach(async () => {
      onChange = jest.fn();
      driver = render(
        <NativeConfiguredDropdown
          options={options}
          onChange={onChange}
          placeholder={placeholder}
        />,
        baseProps['data-hook'],
      ).driver;
    });

    it('should be called when selection changed', async () => {
      expect(await driver.getDropdownText()).toEqual(placeholder);
      await driver.selectOptionAt(2);
      expect(await driver.getDropdownText()).toEqual(options[2].value);
      expect(onChange).toHaveBeenCalledWith(options[2]);
    });

    it('should not be called when native dropdown is readonly', async () => {
      const { driver } = render(
        <NativeConfiguredDropdown options={options} readOnly />,
        baseProps['data-hook'],
      );
      await driver.selectOptionAt(0);
      expect(onChange).not.toHaveBeenCalled();
    });

    it('should not be called when selecting disabled item', async () => {
      await driver.selectOptionAt(4);
      expect(onChange).not.toHaveBeenCalled();
    });

    it('should not be called when re-selecting a selected item', async () => {
      await driver.selectOptionAt(0);
      await driver.selectOptionAt(0);
      expect(onChange).toHaveBeenCalledTimes(1);
    });
  });

  describe('error states', () => {
    it('should have error', async () => {
      const { driver } = render(
        <NativeConfiguredDropdown options={[]} error />,
        baseProps['data-hook'],
      );
      expect(await driver.hasError()).toBeTruthy();
      expect(await driver.hasErrorMessage()).toBeFalsy();
    });

    it('should have error message', async () => {
      const errorMessage = 'Ooops, something went wrong';
      const { driver } = render(
        <NativeConfiguredDropdown error errorMessage={errorMessage} />,
        baseProps['data-hook'],
      );
      const errorIcon = driver.errorIcon;
      const iconButtonDescription = errorIcon.getIconButtonDescription();
      const tooltipContent = errorIcon.getTooltipContent();
      expect(await iconButtonDescription.text()).toBe(errorMessage);
      expect(await tooltipContent.exists()).toBe(false);

      expect(await driver.hasError()).toBeTruthy();
      expect(await driver.hasErrorMessage()).toBeTruthy();
      await expect(() => driver.getErrorMessageContent()).rejects.toThrow();

      await errorIcon.click();

      expect(await iconButtonDescription.exists()).toBe(false);
      expect(await tooltipContent.text()).toBe(errorMessage);

      expect(await driver.hasError()).toBeTruthy();
      expect(await driver.hasErrorMessage()).toBeTruthy();
      expect(await driver.getErrorMessageContent()).toBe(errorMessage);
    });
  });

  describe('disabled', () => {
    it('should be disabled', async () => {
      const { driver } = render(
        <NativeConfiguredDropdown disabled />,
        baseProps['data-hook'],
      );
      expect(await driver.isDisabled()).toBe(true);
    });
  });

  describe('unsupported driver methods', () => {
    it('hasAriaHasPopup', async () => {
      const { driver } = render(
        <NativeConfiguredDropdown />,
        baseProps['data-hook'],
      );
      expect(await driver.getAriaActivedescendant()).toBe(null);
    });

    it('getAriaExpanded', async () => {
      const { driver } = render(
        <NativeConfiguredDropdown />,
        baseProps['data-hook'],
      );
      expect(await driver.getAriaExpanded()).toBe(null);
    });

    it('areOptionsShown', async () => {
      const { driver } = render(
        <NativeConfiguredDropdown />,
        baseProps['data-hook'],
      );
      expect(await driver.areOptionsShown()).toBe(null);
    });

    it('click', async () => {
      const { driver } = render(
        <NativeConfiguredDropdown />,
        baseProps['data-hook'],
      );
      expect(await driver.click()).toBe(null);
    });
  });

  describe('error tooltip', () => {
    const createDriver = createRendererWithUniDriver(dropdownDriverFactory);

    it('should toggle error tooltip when clicking on error icon', async () => {
      const { driver } = createDriver(
        <Dropdown options={[]} error errorMessage="error Message" />,
      );
      expect(await driver.isErrorTooltipShown()).toBeFalsy();

      await driver.clickOnErrorIcon();

      jest.runAllTimers();

      expect(await driver.isErrorTooltipShown()).toBeTruthy();

      await driver.clickOnErrorIcon();

      jest.runAllTimers();

      expect(await driver.isErrorTooltipShown()).toBeFalsy();
    });

    it('should set the correct a11y attributes', async () => {
      const { driver } = createDriver(
        <Dropdown options={[]} error errorMessage="error Message" />,
      );

      await driver.clickOnErrorIcon();

      jest.runAllTimers();

      expect(await driver.isErrorTooltipShown()).toBeTruthy();

      const ariaDescribedBy = await driver.getAriaDescribedBy();
      expect(ariaDescribedBy).toBeDefined();
      const errorMessage = document.querySelector(`#${ariaDescribedBy}`);

      expect(errorMessage).not.toBeNull();
      expect(errorMessage.textContent).toBe('error Message');
    });

    it('aria describedby should be concatenated to value from props', async () => {
      const { driver } = createDriver(
        <Dropdown
          options={[]}
          error
          errorMessage="error Message"
          aria-describedby="some descriptions"
        />,
      );

      await driver.clickOnErrorIcon();

      jest.runAllTimers();

      expect(await driver.isErrorTooltipShown()).toBeTruthy();

      expect(await driver.getAriaDescribedBy()).toContain('some descriptions');

      expect(await driver.getErrorMessageContent()).toBe('error Message');
    });
  });
});
